import { LightningBoltSolidIcon, PeopleIcon, StatusCircleInnerIcon } from '@fluentui/react-icons-mdl2';
import { Tooltip, Dropdown, DropdownProps, Text, Datepicker, CalendarIcon, SvgIconSizeValue } from '@fluentui/react-northstar'
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatepickerLocalizations } from '../../../../App';
import { appState, AppStateContext, AppStateValue } from '../../../../AppState';
import { filterPlatformUsersInRole, getStatusIdByGuid } from '../../../../tikit/ticketHelper';
import { ConvertDateToLocale } from '../../../common/ConvertDate';
import { TicketProps } from '../../../interfaces/ticketproperties.interface';
import { LIFECYCLE_STATUS_GUID, STATUS_GUID } from '../../../utils/constants';
import {
  EditTicketProps
} from '../TicketConversation.interfaces';
import AppCSS from '../TicketConversation.module.css';

interface TicketQuickEditProps extends EditTicketProps {
  teamId?: number | null;
  groupId?: number | null;
  onChange: (data: any) => void;
  ticketData?: any;
  isUserLicensed: boolean;
  isLifecycleEnabled: boolean;
}

const formatStatusToDropdownItems = (status: TicketProps[]): DropdownDataModel[] => {

  const items: DropdownDataModel[] = sortTreeFlat(status).map((s: TicketProps) => (
    {
      key: s.Id.toString(),
      label: s.Value,
      header: s.Value,
      Id: s.Id,
      value: s.Value
    }
  ))
  return items;
}

const iconStyle = {
  size: 'small' as SvgIconSizeValue,
  style: { color: '#8f90a6' }
};

const formatUsersToDropdownItems = (users: PlatformUser[]): DropdownDataModel[] => {
  const items: DropdownDataModel[] = users.map((u: PlatformUser) => (
    {
      key: u.Id.toString(),
      label: u.FullName,
      header: u.FullName,
      Id: u.Id,
      value: u.FullName
    }
  ))
  return items;
}

function sortTreeFlat<T extends { Id: number; ParentId?: number | null; Position?: number }>(items: T[]): T[] {
  const childrenMap = new Map<number | null, T[]>();

  for (const item of items) {
    const key = item.ParentId ?? null;
    if (!childrenMap.has(key)) childrenMap.set(key, []);
    childrenMap.get(key).push(item);
  }

  // Sort each group of siblings by Position
  for (const [, siblings] of childrenMap) {
    siblings.sort((a, b) => a.Position - b.Position);
  }

  const result: T[] = [];

  function walk(parentId: number | null) {
    const children = childrenMap.get(parentId) ?? [];
    for (const child of children) {
      result.push(child);
      walk(child.Id);
    }
  }

  walk(null);
  return result;
}


const setAssigneeList = (groupId: number, teamId: number, currentState: AppStateValue, setAssignees) => {
  if (groupId) {
    const groupMembers = currentState.supportgroups?.find(supportGroup => supportGroup.Id === groupId)?.Members || [];
    const userInRoles = filterPlatformUsersInRole(groupMembers, currentState.usersInRole);
    setAssignees(userInRoles);
  } else if (teamId) {
    const teamMembers = currentState.teams?.find(x => x.Id === teamId)?.Users || [];
    const userInRoles = filterPlatformUsersInRole(teamMembers, currentState.usersInRole);
    setAssignees(userInRoles);
  } else setAssignees(currentState.usersInRole);
};

const TicketQuickEdit: React.FunctionComponent<TicketQuickEditProps> = ({
  StatusId,
  AssigneeId,
  DueDate,
  PriorityId,
  ticketData,
  isUserLicensed,
  teamId,
  groupId,
  onChange,
  isLifecycleEnabled
}) => {
  const { t } = useTranslation();
  const currentState = appState();
  const [assignees, setAssignees] = useState<PlatformUser[]>([]);
  const [assigneeName, setAssigneeName] = useState<string>('');
  const [data, setData] = useState<EditTicketProps>({
    StatusId: StatusId,
    AssigneeId: AssigneeId,
    DueDate: DueDate,
    PriorityId: PriorityId
  })

  useEffect(() => {
    setData({ StatusId, AssigneeId, DueDate, PriorityId });
    onChange({ StatusId, AssigneeId, DueDate, PriorityId });
  }, [StatusId, AssigneeId, DueDate, PriorityId]);

  useEffect(() => setAssigneeList(groupId, teamId, currentState, setAssignees), [teamId, groupId, currentState.usersInRole, currentState.supportgroups, currentState.teams]);
  useEffect(() => setAssigneeName(currentState.usersInRole?.find(o => o.Id == AssigneeId)?.FullName || ''), [AssigneeId]);

  const customDateFormatter = (date: any) => ConvertDateToLocale(date, DateTime.DATE_SHORT);

  const customDateFormatterCallback = useCallback((date: any) => customDateFormatter(date), [customDateFormatter]);

  const onChangeAssignee = (selectedItem: DropdownProps) => {
    const value: any = selectedItem.value;
    const assigneeId = value ? parseInt(value.key) : null;
    setData(d => ({ ...d, AssigneeId: assigneeId }));
    onChange(({ ...data, AssigneeId: assigneeId }));
    if (!value) setAssigneeName('');
  }

  const onChangeStatus = (_e, p) => {
    const newData: any = p.value;
    setData(d => ({ ...d, StatusId: parseInt(newData.key) }))
    onChange(({ ...data, StatusId: parseInt(newData.key) }));
  }

  const onChangeStatusCallback = useCallback((_e, p) => { onChangeStatus(_e, p); }, [onChangeStatus]);

  const onSearchQueryChangeAssignee = (_e, data) => setAssigneeName(data.searchQuery);

  const onSearchQueryChangeAssigneeCallback = useCallback((_e, data) => { onSearchQueryChangeAssignee(_e, data); }, [onSearchQueryChangeAssignee]);

  const onChangeAssigneeDropdown = (_e, p) => onChangeAssignee(p);

  const onChangeAssigneeDropdownCallback = useCallback((_e, p) => { onChangeAssigneeDropdown(_e, p); }, [onChangeAssigneeDropdown]);

  const onDateChange = (_e, p) => {
    setData(d => ({ ...d, DueDate: (new Date(p.value)).toISOString() }));
    onChange(({ ...data, DueDate: (new Date(p.value)).toISOString() }));
  };

  const onDateChangeCallback = useCallback((_e, p) => { onDateChange(_e, p); }, [onDateChange]);

  const onChangePriorityDropdown = (_e, p) => {
    const newData: any = p.value;
    setData(d => ({ ...d, PriorityId: parseInt(newData.key) }));
    onChange(({ ...data, PriorityId: parseInt(newData.key) }));
  };

  const onChangePriorityDropdownCallback = useCallback((_e, p) => { onChangePriorityDropdown(_e, p); }, [onChangePriorityDropdown]);

  const getStatusDropdownItems = (ticketStatus: TicketProps[] = []) => {
    const items = ticketStatus.filter((item: any) => {
      const initialCondition = item.Id != getStatusIdByGuid(currentState.ticketStatus, STATUS_GUID.DEFLECTED);
      const lifecycleIsActive = (ticketData?.TicketLifecycle?.Status ?? false) &&
        [LIFECYCLE_STATUS_GUID.FAILED, LIFECYCLE_STATUS_GUID.COMPLETED].indexOf(ticketData?.TicketLifecycle.Status.Guid) < 0;
      const lifecycleCondition = isLifecycleEnabled && lifecycleIsActive &&
        (item.Id == getStatusIdByGuid(currentState.ticketStatus, STATUS_GUID.CLOSED) ||
          item.Id == getStatusIdByGuid(currentState.ticketStatus, STATUS_GUID.RESOLVED));
      return initialCondition && !lifecycleCondition;
    })

    return formatStatusToDropdownItems(items)
  }

  const getStatusDropdownValue = (ticketStatus: TicketProps[] = []) => {
    const values: DropdownDataModel[] = formatStatusToDropdownItems(ticketStatus)
    return values.find(ddm => ddm.Id === data.StatusId)?.value ?? ticketData?.Status?.Value
  }

  const renderStatusDropdown = (ticketStatus: TicketProps[] = []) => {
    return (
      <Dropdown
        className='mr-1'
        disabled={!isUserLicensed}
        items={getStatusDropdownItems(ticketStatus)}
        value={getStatusDropdownValue(ticketStatus)}
        placeholder={t("ticket-details.ticket-conversation.status-dropdown-placeholder")}
        checkable
        inverted
        fluid
        getA11ySelectionMessage={{
          onAdd: item => t('ticket-details.ticket-conversation.item-has-been-selected', { item: item }),
        }}
        onChange={onChangeStatusCallback}
        styles={{ width: 'calc(100% - 1em)' }}
        list={{
          styles: {
            overflow: 'auto !important'
          },
        }}
      />
    )
  }

  return (
    <AppStateContext.Consumer>
      {(state) => {
        return (
          <React.Fragment>
            <div className='flex flex-wrap flex-grow px-2'>
              <div className={`${AppCSS.WMobileAdjust} flex items-center`}>
                <Tooltip
                  trigger={<LightningBoltSolidIcon {...iconStyle} />}
                  content={t("ticket-details.ticket-conversation.status")}
                  position='above'
                />
                {renderStatusDropdown(state.ticketStatus)}
              </div>
              <div className={`${AppCSS.WMobileAdjust} flex items-center`}>
                <Tooltip
                  trigger={<PeopleIcon {...iconStyle} />}
                  content={t("ticket-details.ticket-conversation.assignee")}
                  position='above'
                />
                <Dropdown className='mr-1'
                  disabled={!isUserLicensed}
                  items={formatUsersToDropdownItems(assignees)}
                  value={formatUsersToDropdownItems(assignees)?.filter(u => u.Id === data.AssigneeId)}
                  searchQuery={assigneeName}
                  search
                  clearable={isUserLicensed}
                  checkable
                  inverted
                  fluid
                  getA11ySelectionMessage={{
                    onAdd: item => t('ticket-details.ticket-conversation.item-has-been-selected', { item: item }),
                  }}
                  onSearchQueryChange={onSearchQueryChangeAssigneeCallback}
                  onChange={onChangeAssigneeDropdownCallback}
                  noResultsMessage={
                    <div style={{ textAlign: 'center' }}>
                      <Text content={`User not found`} />
                    </div>
                  }
                />
              </div>
              <div className={`${AppCSS.WMobileAdjust} flex items-center`}>
                <Tooltip
                  trigger={<CalendarIcon {...iconStyle} />}
                  content={t("ticket-details.ticket-conversation.due-date")}
                  position='above'
                />
                <Datepicker
                  {...DatepickerLocalizations}
                  disabled={!isUserLicensed}
                  firstDayOfWeek={0}
                  className={`datepicker-transparent date-no-icon mr-1`}
                  formatMonthDayYear={customDateFormatterCallback}
                  onDateChange={onDateChangeCallback}
                  {...(data.DueDate && {
                    selectedDate: new Date(data.DueDate)
                  })}
                />
              </div>
              <div className={`${AppCSS.WMobileAdjust} flex items-center`}>
                <Tooltip
                  trigger={<StatusCircleInnerIcon {...iconStyle} />}
                  content={t("ticket-details.ticket-conversation.priority")}
                  position='above'
                />
                <Dropdown
                  disabled={!isUserLicensed}
                  items={formatStatusToDropdownItems(sortTreeFlat(state.priority))}
                  value={formatStatusToDropdownItems(state.priority).find(u => u.Id === data.PriorityId)?.value ?? ticketData?.Priority?.Value}
                  placeholder={t("ticket-details.ticket-conversation.priority")}
                  checkable
                  inverted
                  fluid
                  getA11ySelectionMessage={{
                    onAdd: item => t('ticket-details.ticket-conversation.item-has-been-selected', { item: item }),
                  }}
                  onChange={onChangePriorityDropdownCallback}
                  styles={{ width: 'calc(100% - 1em)' }}
                />
              </div>
            </div>
          </React.Fragment>
        )
      }}
    </AppStateContext.Consumer>
  )
}

export default TicketQuickEdit;